import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { bibleState } from '@/state/bible-state';
import {
  BookmarkIcon,
  NoteIcon,
  PrayIcon,
  ClockIcon,
  SlidersIcon,
  HelpCircleIcon,
  SearchIcon,
  WarningIcon,
  CloseIcon,
} from '@components/icons';
import { BookmarksContent } from './bookmarks-content';
import { NotesContent } from './notes-content';
import { PrayerContent } from './prayer-content';
import { HistoryContent } from './history-content';
import { SettingsContent } from './settings-content';
import { HelpContent } from './help-content';
import { SearchContent } from '../search-panel/search-content';
import { ProblemsContent } from './problems-content';
import { isDesktop } from '@/desktop/desktop';

/**
 * Unified user data panel - combines bookmarks, prayer, history, and import/export.
 */
export const UserDataPanel = observer(function UserDataPanel() {
  const [shouldRender, setShouldRender] = useState(false);
  const [animationState, setAnimationState] = useState<'entering' | 'exiting' | 'visible'>(
    'exiting',
  );

  const isVisible = bibleState.userDataPanelVisible;
  const isChatOpen = bibleState.chatPanelVisible;
  const activeTab = bibleState.userDataPanelTab;

  // Handle visibility changes with animation
  useEffect(() => {
    if (isVisible) {
      setShouldRender(true);
      setAnimationState('entering');
      const timer = setTimeout(() => setAnimationState('visible'), 400);
      return () => clearTimeout(timer);
    } else {
      setAnimationState('exiting');
      const timer = setTimeout(() => setShouldRender(false), 300);
      return () => clearTimeout(timer);
    }
  }, [isVisible]);

  if (!shouldRender) return null;

  return (
    <div
      className={`user-data-panel ${isChatOpen ? 'split-mode' : ''} ${animationState === 'entering' ? 'entering' : ''} ${animationState === 'exiting' ? 'closing' : ''}`}
    >
      {/* Header with segmented control */}
      <div className="user-data-panel-header">
        <div className="user-data-segment-control">
          <button
            onClick={() => bibleState.setUserDataPanelTab('search')}
            className={`user-data-segment ${activeTab === 'search' ? 'active' : ''}`}
            title="Search"
          >
            <SearchIcon className="h-5 w-5" filled />
          </button>
          {/* Bookmarks, notes, prayer, and settings are native-only
              (DESKTOP-PRD §3.8) — web keeps search, history, problems. */}
          {isDesktop() && (
            <button
              onClick={() => bibleState.setUserDataPanelTab('bookmarks')}
              className={`user-data-segment ${activeTab === 'bookmarks' ? 'active' : ''}`}
              title="Bookmarks"
            >
              <BookmarkIcon className="h-5 w-5" filled />
            </button>
          )}
          {isDesktop() && (
            <button
              onClick={() => bibleState.setUserDataPanelTab('notes')}
              className={`user-data-segment ${activeTab === 'notes' ? 'active' : ''}`}
              title="Notes"
            >
              <NoteIcon className="h-5 w-5" filled />
            </button>
          )}
          {isDesktop() && (
            <button
              onClick={() => bibleState.setUserDataPanelTab('prayer')}
              className={`user-data-segment ${activeTab === 'prayer' ? 'active' : ''}`}
              title="Prayer List"
            >
              <PrayIcon className="h-5 w-5" />
            </button>
          )}
          <button
            onClick={() => bibleState.setUserDataPanelTab('history')}
            className={`user-data-segment ${activeTab === 'history' ? 'active' : ''}`}
            title="Reading History"
          >
            <ClockIcon className="h-5 w-5" filled />
          </button>
          {/* Problems sits right of History (UAT 2026-08-28) */}
          <button
            onClick={() => bibleState.setUserDataPanelTab('problems')}
            className={`user-data-segment ${activeTab === 'problems' ? 'active' : ''}`}
            title="Problems"
          >
            <WarningIcon className="h-5 w-5" filled />
          </button>
          {/* Settings stays everywhere — on web it holds Display only
              (theme/font/reading mode); Import + Storage are native-only */}
          <button
            onClick={() => bibleState.setUserDataPanelTab('settings')}
            className={`user-data-segment ${activeTab === 'settings' ? 'active' : ''}`}
            title="Settings"
          >
            <SlidersIcon className="h-5 w-5" filled />
          </button>
          {/* The guided tour walks study creation — native-only surface */}
          {isDesktop() && (
            <button
              onClick={() => bibleState.startGuide()}
              className={`user-data-segment ${activeTab === 'guide' ? 'active' : ''}`}
              title="Help & Guided Tour"
            >
              <HelpCircleIcon className="h-5 w-5" filled />
            </button>
          )}
        </div>
        <button
          onClick={() => bibleState.hideUserDataPanel()}
          className="-m-1 shrink-0 rounded-full p-2 text-stone-500 transition-all hover:bg-black/5 hover:text-stone-700 dark:text-stone-400 dark:hover:bg-white/10 dark:hover:text-stone-200"
        >
          <CloseIcon className="h-5 w-5" />
        </button>
      </div>

      {/* Tab content — native-only tabs never mount on web (§3.8) */}
      <div className="user-data-panel-content">
        {isDesktop() && activeTab === 'bookmarks' && <BookmarksContent />}
        {isDesktop() && activeTab === 'notes' && <NotesContent />}
        {isDesktop() && activeTab === 'prayer' && <PrayerContent />}
        {activeTab === 'history' && <HistoryContent />}
        {activeTab === 'settings' && <SettingsContent />}
        {activeTab === 'search' && <SearchContent />}
        {activeTab === 'problems' && <ProblemsContent />}
        {isDesktop() && activeTab === 'guide' && <HelpContent />}
      </div>
    </div>
  );
});

export default UserDataPanel;
